import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Lock, LogOut, Loader2, Plus, Pencil, Trash2, Save, X, Users, Wallet, Car,
  Shield, BookOpen, ShieldCheck, Layers, ListTree, UserCog, Check,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail) => {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const STATUS_TONE = {
  active: 'bg-green-50 text-green-700 border-green-200',
  inactive: 'bg-gray-100 text-gray-600 border-gray-200',
  locked: 'bg-red-50 text-red-700 border-red-200',
};
const FLAGS = [
  { key: 'can_view', label: 'View' },
  { key: 'can_create', label: 'Create' },
  { key: 'can_edit', label: 'Edit' },
  { key: 'can_delete', label: 'Delete' },
];

const AccessControlPage = () => {
  const navigate = useNavigate();
  const { user, ready, logout, isAdmin } = useAuth();

  const [tab, setTab] = useState('users');
  const [levels, setLevels] = useState([]);
  const [components, setComponents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (ready && !user) navigate('/admin/login?next=/admin/access', { replace: true });
  }, [ready, user, navigate]);

  const loadAll = async () => {
    setLoading(true); setError('');
    try {
      const [lv, cp, us] = await Promise.all([
        axios.get(`${API}/admin/access-levels`),
        axios.get(`${API}/admin/access-components`),
        axios.get(`${API}/admin/users`),
      ]);
      setLevels(lv.data); setComponents(cp.data); setUsers(us.data);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (ready && user && isAdmin) loadAll(); /* eslint-disable-next-line */ }, [ready, user, isAdmin]);

  const levelById = useMemo(() => Object.fromEntries(levels.map((l) => [l.id, l])), [levels]);

  if (!ready || (ready && user && loading && isAdmin)) {
    return <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center"><Loader2 className="text-brand-yellow animate-spin" size={22} /></div>;
  }
  if (ready && user && !isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center px-6" data-testid="access-denied">
        <div className="bg-white p-10 max-w-md text-center">
          <Lock className="mx-auto text-gray-400 mb-3" size={28} />
          <h1 className="font-heading text-xl font-bold text-gray-900 mb-2">Administrator access required</h1>
          <p className="text-sm text-gray-600 mb-6">You don&apos;t have permission to manage users and access control.</p>
          <Button onClick={() => navigate('/team')} className="bg-gray-900 text-white rounded-none">Back to dashboard</Button>
        </div>
      </div>
    );
  }

  const TABS = [
    { key: 'users', label: 'Users', icon: UserCog },
    { key: 'roles', label: 'Roles & Permissions', icon: Layers },
    { key: 'components', label: 'Menu Components', icon: ListTree },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="access-control-page">
      {/* Top bar */}
      <div className="bg-[#0a0f1e] border-b border-white/10 text-white sticky top-0 z-30">
        <div className="max-w-[1720px] mx-auto px-[3%] py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 flex items-center justify-center"><ShieldCheck size={14} className="text-brand-yellow" /></div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-brand-yellow uppercase">Admin Dashboard</div>
              <div className="text-sm font-medium">User &amp; Access Control</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/team')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2"><Users size={13} /> Team</button>
            <button onClick={() => navigate('/admin/cashflow')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2"><Wallet size={13} /> Cashflow</button>
            <button onClick={() => navigate('/admin/policies')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2"><BookOpen size={13} /> Policies</button>
            <span className="hidden md:block text-xs text-white/50 border-l border-white/10 pl-3">{user.email}</span>
            <button onClick={async () => { await logout(); navigate('/'); }} className="inline-flex items-center gap-1.5 bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 text-xs font-semibold px-3 py-2"><LogOut size={13} /> Log out</button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative bg-[#0a0f1e] pt-8 pb-8 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ffffff18_1px,transparent_1px),linear-gradient(to_bottom,#ffffff18_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />
        <div className="relative max-w-[1720px] mx-auto px-[3%]">
          <div className="inline-flex items-center gap-2 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 mb-3">
            <Lock size={13} className="text-brand-yellow" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">Internal · R S T &amp; Co.</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-white leading-[1.05] tracking-tight">User &amp; Access Control</h1>
          <p className="text-sm text-white/60 mt-2 max-w-2xl">Manage internal users, assign roles, and set exactly which modules each role can view, create, edit and delete.</p>
        </div>
      </section>

      <div className="flex-1 max-w-[1720px] w-full mx-auto px-[3%] pt-6 pb-16">
        {/* Tabs */}
        <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-6">
          {TABS.map((t) => {
            const Icon = t.icon; const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${active ? 'border-gray-900 text-gray-900 font-semibold' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                data-testid={`ac-tab-${t.key}`}>
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        {error && <div className="border border-red-300 bg-red-50 text-red-800 text-sm px-4 py-2 mb-4" data-testid="ac-error">{error}</div>}

        {tab === 'users' && <UsersTab users={users} levels={levels} levelById={levelById} reload={loadAll} setError={setError} />}
        {tab === 'roles' && <RolesTab levels={levels} components={components} reload={loadAll} setError={setError} />}
        {tab === 'components' && <ComponentsTab components={components} reload={loadAll} setError={setError} />}
      </div>

      <style>{`.admin-input { width: 100%; border: 1px solid #d1d5db; padding: 10px 12px; font-size: 14px; outline: none; background: white; }
        .admin-input:focus { border-color: #111827; }`}</style>
    </div>
  );
};

// ===================== USERS TAB =========================================== //
const emptyUser = { name: '', email: '', phone: '', status: 'active', password: '', access_level_ids: [] };

const UsersTab = ({ users, levels, levelById, reload, setError }) => {
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEditing({ ...emptyUser }); setIsNew(true); };
  const openEdit = (u) => { setEditing({ ...emptyUser, ...u, password: '' }); setIsNew(false); };
  const close = () => { setEditing(null); };

  const toggleLevel = (id) => setEditing((e) => ({
    ...e, access_level_ids: e.access_level_ids.includes(id) ? e.access_level_ids.filter((x) => x !== id) : [...e.access_level_ids, id],
  }));

  const save = async () => {
    setSaving(true); setError('');
    try {
      const body = { name: editing.name, phone: editing.phone, status: editing.status, access_level_ids: editing.access_level_ids };
      if (editing.password) body.password = editing.password;
      if (isNew) { body.email = editing.email; await axios.post(`${API}/admin/users`, body); }
      else { await axios.put(`${API}/admin/users/${editing.id}`, body); }
      close(); await reload();
    } catch (err) { setError(formatError(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user "${u.name}"? This cannot be undone.`)) return;
    setError('');
    try { await axios.delete(`${API}/admin/users/${u.id}`); await reload(); }
    catch (err) { setError(formatError(err?.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-black text-gray-900">Internal Users · {users.length}</h2>
        <Button onClick={openNew} className="bg-gray-900 hover:bg-black text-white rounded-none" data-testid="add-user-btn"><Plus size={14} className="mr-1.5" /> New user</Button>
      </div>

      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 text-left">
            <tr className="text-[11px] font-mono uppercase tracking-widest text-gray-500">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Roles</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium text-gray-900">{u.name}</td>
                <td className="px-4 py-3 text-gray-600">{u.email}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(u.access_level_ids || []).map((id) => levelById[id] && (
                      <span key={id} className="text-[10px] font-mono px-1.5 py-0.5 border border-gray-300 text-gray-600">{levelById[id].level_name}</span>
                    ))}
                    {(u.access_level_ids || []).length === 0 && <span className="text-xs text-gray-400">—</span>}
                  </div>
                </td>
                <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 border capitalize ${STATUS_TONE[u.status] || STATUS_TONE.inactive}`}>{u.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(u)} className="p-2 border border-gray-200 hover:border-gray-900 hover:bg-gray-100" data-testid={`edit-user-${u.id}`}><Pencil size={13} /></button>
                    <button onClick={() => remove(u)} className="p-2 border border-red-100 text-red-600 hover:border-red-500 hover:bg-red-50" data-testid={`delete-user-${u.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 bg-black/50 flex justify-end" onClick={close} data-testid="user-editor">
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div className="font-heading text-lg font-bold text-gray-900">{isNew ? 'New user' : editing.name}</div>
              <div className="flex items-center gap-2">
                <Button onClick={save} disabled={saving} className="bg-gray-900 hover:bg-black text-white rounded-none" data-testid="save-user-btn">
                  {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</> : <><Save size={14} className="mr-2" /> Save</>}
                </Button>
                <button onClick={close} className="p-2 border border-gray-200 hover:border-gray-900"><X size={16} /></button>
              </div>
            </div>
            <div className="p-6 space-y-5">
              <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Full name</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="admin-input" data-testid="user-name" /></div>
              <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Email</label>
                <input value={editing.email} disabled={!isNew} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="admin-input disabled:bg-gray-100 disabled:text-gray-500" data-testid="user-email" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Phone</label>
                  <input value={editing.phone || ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="admin-input" data-testid="user-phone" /></div>
                <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Status</label>
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="admin-input" data-testid="user-status">
                    <option value="active">Active</option><option value="inactive">Inactive</option><option value="locked">Locked</option>
                  </select></div>
              </div>
              <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">{isNew ? 'Password' : 'Reset password (leave blank to keep)'}</label>
                <input type="password" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="admin-input" placeholder="At least 8 characters" data-testid="user-password" /></div>
              <div>
                <label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-2">Roles</label>
                <div className="space-y-2">
                  {levels.map((l) => (
                    <label key={l.id} className="flex items-center gap-3 border border-gray-200 px-3 py-2 cursor-pointer hover:bg-gray-50" data-testid={`user-role-${l.id}`}>
                      <input type="checkbox" checked={editing.access_level_ids.includes(l.id)} onChange={() => toggleLevel(l.id)} className="accent-gray-900" />
                      <span className="text-sm font-medium text-gray-800">{l.level_name}</span>
                      <span className="text-[10px] font-mono text-gray-400 ml-auto">rank {l.level_rank}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== ROLES & PERMISSIONS TAB ============================= //
const RolesTab = ({ levels, components, reload, setError }) => {
  const [selected, setSelected] = useState(null);
  const [perms, setPerms] = useState({});      // component_id -> flags
  const [loadingP, setLoadingP] = useState(false);
  const [savingP, setSavingP] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [isNewLevel, setIsNewLevel] = useState(false);

  const leafComponents = useMemo(() => components.filter((c) => c.parent_component_id), [components]);
  const parents = useMemo(() => components.filter((c) => !c.parent_component_id), [components]);

  useEffect(() => { if (!selected && levels.length) setSelected(levels[0].id); }, [levels, selected]);

  const selectedLevel = levels.find((l) => l.id === selected);
  const isSuperSelected = selectedLevel?.level_rank === 1;

  useEffect(() => {
    const loadPerms = async () => {
      if (!selected) return;
      setLoadingP(true);
      try {
        const { data } = await axios.get(`${API}/admin/permissions`, { params: { access_level_id: selected } });
        const map = {};
        data.forEach((r) => { map[r.component_id] = { can_view: r.can_view, can_create: r.can_create, can_edit: r.can_edit, can_delete: r.can_delete }; });
        setPerms(map);
      } catch (err) { setError(formatError(err?.response?.data?.detail)); }
      finally { setLoadingP(false); }
    };
    loadPerms();
  }, [selected, setError]);

  const toggle = (cid, flag) => setPerms((p) => {
    const cur = p[cid] || { can_view: false, can_create: false, can_edit: false, can_delete: false };
    const next = { ...cur, [flag]: !cur[flag] };
    if (flag !== 'can_view' && next[flag]) next.can_view = true;          // any write implies view
    if (flag === 'can_view' && !next.can_view) { next.can_create = next.can_edit = next.can_delete = false; }
    return { ...p, [cid]: next };
  });

  const savePerms = async () => {
    setSavingP(true); setError('');
    try {
      const entries = leafComponents.map((c) => ({ component_id: c.id, ...(perms[c.id] || { can_view: false, can_create: false, can_edit: false, can_delete: false }) }));
      await axios.put(`${API}/admin/permissions`, { access_level_id: selected, entries });
    } catch (err) { setError(formatError(err?.response?.data?.detail)); }
    finally { setSavingP(false); }
  };

  const saveLevel = async () => {
    setError('');
    try {
      const body = { level_name: editingLevel.level_name, level_rank: Number(editingLevel.level_rank), description: editingLevel.description || '' };
      if (isNewLevel) await axios.post(`${API}/admin/access-levels`, body);
      else await axios.put(`${API}/admin/access-levels/${editingLevel.id}`, body);
      setEditingLevel(null); await reload();
    } catch (err) { setError(formatError(err?.response?.data?.detail)); }
  };
  const removeLevel = async (l) => {
    if (!window.confirm(`Delete role "${l.level_name}"?`)) return;
    setError('');
    try { await axios.delete(`${API}/admin/access-levels/${l.id}`); if (selected === l.id) setSelected(null); await reload(); }
    catch (err) { setError(formatError(err?.response?.data?.detail)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6" data-testid="roles-tab">
      {/* Roles list */}
      <div className="border border-gray-200">
        <div className="flex items-center justify-between bg-gray-900 text-white px-4 py-3">
          <span className="font-mono text-[10px] tracking-widest uppercase">Roles</span>
          <button onClick={() => { setEditingLevel({ level_name: '', level_rank: (Math.max(0, ...levels.map((l) => l.level_rank)) + 1), description: '' }); setIsNewLevel(true); }} className="text-brand-yellow hover:text-white" data-testid="add-role-btn"><Plus size={15} /></button>
        </div>
        {levels.map((l) => (
          <div key={l.id} className={`border-b border-gray-100 px-4 py-3 cursor-pointer group ${selected === l.id ? 'bg-brand-yellow/10 border-l-4 border-l-brand-yellow' : 'hover:bg-gray-50 border-l-4 border-l-transparent'}`}
            onClick={() => setSelected(l.id)} data-testid={`role-item-${l.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-semibold text-gray-900 text-sm">{l.level_name}</div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                <button onClick={(e) => { e.stopPropagation(); setEditingLevel({ ...l }); setIsNewLevel(false); }} className="p-1 text-gray-500 hover:text-gray-900"><Pencil size={12} /></button>
                {l.level_rank !== 1 && <button onClick={(e) => { e.stopPropagation(); removeLevel(l); }} className="p-1 text-red-500 hover:text-red-700"><Trash2 size={12} /></button>}
              </div>
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">Rank {l.level_rank} · {l.user_count || 0} user{(l.user_count || 0) === 1 ? '' : 's'}</div>
          </div>
        ))}
      </div>

      {/* Permission matrix */}
      <div className="border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="font-heading text-lg font-black text-gray-900">{selectedLevel?.level_name || 'Select a role'} <span className="text-sm font-normal text-gray-500">— permissions</span></div>
            {selectedLevel?.description && <div className="text-xs text-gray-500 mt-0.5">{selectedLevel.description}</div>}
          </div>
          {selected && !isSuperSelected && (
            <Button onClick={savePerms} disabled={savingP} className="bg-gray-900 hover:bg-black text-white rounded-none" data-testid="save-perms-btn">
              {savingP ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</> : <><Save size={14} className="mr-2" /> Save permissions</>}
            </Button>
          )}
        </div>

        {isSuperSelected ? (
          <div className="p-8 text-center text-sm text-gray-600 flex flex-col items-center gap-2">
            <ShieldCheck className="text-green-600" size={24} />
            Super Admin always has full access to every module. Permissions can&apos;t be restricted for this role.
          </div>
        ) : loadingP ? (
          <div className="p-12 text-center"><Loader2 className="animate-spin mx-auto text-gray-400" size={20} /></div>
        ) : (
          <table className="w-full text-sm" data-testid="perm-matrix">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-[11px] font-mono uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3 text-left">Module</th>
                {FLAGS.map((f) => <th key={f.key} className="px-4 py-3 text-center w-24">{f.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {parents.map((parent) => {
                const kids = leafComponents.filter((c) => c.parent_component_id === parent.id);
                if (!kids.length) return null;
                return (
                  <React.Fragment key={parent.id}>
                    <tr className="bg-gray-50/60"><td colSpan={5} className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-gray-400">{parent.component_name}</td></tr>
                    {kids.map((c) => (
                      <tr key={c.id} className="border-b border-gray-100" data-testid={`perm-row-${c.slug}`}>
                        <td className="px-4 py-3 font-medium text-gray-800">{c.component_name}</td>
                        {FLAGS.map((f) => {
                          const on = !!perms[c.id]?.[f.key];
                          return (
                            <td key={f.key} className="px-4 py-3 text-center">
                              <button onClick={() => toggle(c.id, f.key)} data-testid={`perm-${c.slug}-${f.key}`}
                                className={`w-6 h-6 border inline-flex items-center justify-center transition-colors ${on ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-300 text-transparent hover:border-gray-500'}`}>
                                <Check size={14} />
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Role editor modal */}
      {editingLevel && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center px-4" onClick={() => setEditingLevel(null)}>
          <div className="bg-white w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="role-editor">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-gray-900">{isNewLevel ? 'New role' : 'Edit role'}</h3>
              <button onClick={() => setEditingLevel(null)} className="p-1"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Role name</label>
                <input value={editingLevel.level_name} onChange={(e) => setEditingLevel({ ...editingLevel, level_name: e.target.value })} className="admin-input" data-testid="role-name" /></div>
              <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Rank (1 = highest)</label>
                <input type="number" min={1} value={editingLevel.level_rank} onChange={(e) => setEditingLevel({ ...editingLevel, level_rank: e.target.value })} className="admin-input" data-testid="role-rank" /></div>
              <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Description</label>
                <textarea value={editingLevel.description || ''} onChange={(e) => setEditingLevel({ ...editingLevel, description: e.target.value })} className="admin-input min-h-[70px]" data-testid="role-description" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditingLevel(null)} className="px-4 py-2 border border-gray-300 text-sm">Cancel</button>
              <Button onClick={saveLevel} className="bg-gray-900 text-white rounded-none" data-testid="save-role-btn"><Save size={14} className="mr-2" /> Save role</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== COMPONENTS TAB ====================================== //
const emptyComp = { component_name: '', slug: '', parent_component_id: '', route: '', icon: '', sort_order: 0 };

const ComponentsTab = ({ components, reload, setError }) => {
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const parents = components.filter((c) => !c.parent_component_id);

  const openNew = () => { setEditing({ ...emptyComp }); setIsNew(true); };
  const openEdit = (c) => { setEditing({ ...c, parent_component_id: c.parent_component_id || '' }); setIsNew(false); };

  const save = async () => {
    setError('');
    try {
      const body = { ...editing, parent_component_id: editing.parent_component_id || null, sort_order: Number(editing.sort_order) || 0 };
      if (isNew) await axios.post(`${API}/admin/access-components`, body);
      else await axios.put(`${API}/admin/access-components/${editing.id}`, body);
      setEditing(null); await reload();
    } catch (err) { setError(formatError(err?.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete component "${c.component_name}"?`)) return;
    setError('');
    try { await axios.delete(`${API}/admin/access-components/${c.id}`); await reload(); }
    catch (err) { setError(formatError(err?.response?.data?.detail)); }
  };

  return (
    <div data-testid="components-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-black text-gray-900">Menu Components</h2>
        <Button onClick={openNew} className="bg-gray-900 hover:bg-black text-white rounded-none" data-testid="add-component-btn"><Plus size={14} className="mr-1.5" /> New component</Button>
      </div>

      <div className="border border-gray-200">
        {parents.map((p) => {
          const kids = components.filter((c) => c.parent_component_id === p.id);
          return (
            <div key={p.id} className="border-b border-gray-100">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50" data-testid={`comp-row-${p.slug}`}>
                <div className="flex items-center gap-2"><ListTree size={14} className="text-gray-500" /><span className="font-semibold text-gray-900 text-sm">{p.component_name}</span><span className="text-[10px] font-mono text-gray-400">/{p.slug}</span></div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(p)} className="p-1.5 border border-gray-200 hover:border-gray-900"><Pencil size={12} /></button>
                  <button onClick={() => remove(p)} className="p-1.5 border border-red-100 text-red-600 hover:border-red-500"><Trash2 size={12} /></button>
                </div>
              </div>
              {kids.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-4 py-2.5 pl-10 border-t border-gray-50" data-testid={`comp-row-${c.slug}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-800">{c.component_name}</span>
                    <span className="text-[10px] font-mono text-gray-400">/{c.slug}</span>
                    {c.route && <span className="text-[10px] font-mono text-brand-blue">{c.route}</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(c)} className="p-1.5 border border-gray-200 hover:border-gray-900"><Pencil size={12} /></button>
                    <button onClick={() => remove(c)} className="p-1.5 border border-red-100 text-red-600 hover:border-red-500"><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="component-editor">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-gray-900">{isNew ? 'New component' : 'Edit component'}</h3>
              <button onClick={() => setEditing(null)} className="p-1"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Name</label>
                <input value={editing.component_name} onChange={(e) => setEditing({ ...editing, component_name: e.target.value })} className="admin-input" data-testid="comp-name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Slug</label>
                  <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') })} className="admin-input font-mono" data-testid="comp-slug" /></div>
                <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Sort order</label>
                  <input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} className="admin-input" data-testid="comp-sort" /></div>
              </div>
              <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Parent</label>
                <select value={editing.parent_component_id} onChange={(e) => setEditing({ ...editing, parent_component_id: e.target.value })} className="admin-input" data-testid="comp-parent">
                  <option value="">— None (top level) —</option>
                  {parents.filter((p) => p.id !== editing.id).map((p) => <option key={p.id} value={p.id}>{p.component_name}</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Route</label>
                  <input value={editing.route || ''} onChange={(e) => setEditing({ ...editing, route: e.target.value })} className="admin-input font-mono" placeholder="/admin/…" data-testid="comp-route" /></div>
                <div><label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">Icon</label>
                  <input value={editing.icon || ''} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} className="admin-input" placeholder="lucide name" data-testid="comp-icon" /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 text-sm">Cancel</button>
              <Button onClick={save} className="bg-gray-900 text-white rounded-none" data-testid="save-component-btn"><Save size={14} className="mr-2" /> Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessControlPage;
